import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Grid2 as Grid, Card, CardContent, TextField, MenuItem,
  CircularProgress, Alert, Tabs, Tab, Chip, IconButton, Tooltip, Button,
} from '@mui/material';
import {
  TrendingUp, TrendingDown, AttachMoney, People, Receipt, Speed,
  PhoneAndroid, Refresh, BarChart as BarIcon, Storage,
} from '@mui/icons-material';
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, Legend, ResponsiveContainer, AreaChart, Area,
  LineChart, Line,
} from 'recharts';
import api from '../../services/api';

const COLORS = ['#6B46C1', '#00C49F', '#FFBB28', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8'];
const STATUS_COLORS: Record<string, string> = { PAID: '#22c55e', PENDING: '#f59e0b', FAILED: '#ef4444', CANCELLED: '#94a3b8' };

const formatCVE = (v: number) => `${Math.round(v || 0).toLocaleString('pt-PT')}$`;
const formatNum = (v: number) => Math.round(v || 0).toLocaleString('pt-PT');

interface BIData {
  kpis: {
    totalTransacoes: number; totalPagas: number; totalPendentes: number; totalFalhadas: number;
    receitaTotal: number; ticketMedio: number; taxaSucesso: number; novosUtilizadores: number;
  };
  daily: { dia: string; transacoes: number; pagas: number; receita: number }[];
  byEntity: { name: string; transacoes: number; pagas: number; receita: number }[];
  byStatus: { name: string; total: number; valor: number }[];
  byDevice: { name: string; total: number; receita: number }[];
  byReferer: { name: string; total: number; receita: number }[];
  byType: { name: string; total: number; receita: number }[];
  byHour: { hora: number; total: number }[];
  yearly: { ano: number; transacoes: number; pagas: number; receita: number; novosUsers: number; crescimentoTx: number | null; crescimentoReceita: number | null }[];
}

const KPICard: React.FC<{ title: string; value: string; subtitle?: string; icon: React.ReactNode; color: string; trend?: number }> = ({ title, value, subtitle, icon, color, trend }) => (
  <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', height: '100%' }}>
    <CardContent sx={{ p: 2.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={500}>{title}</Typography>
          <Typography variant="h5" fontWeight={800} sx={{ mt: 0.5, color }}>{value}</Typography>
          {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
        </Box>
        <Box sx={{ p: 1, borderRadius: 2, bgcolor: `${color}15` }}>{icon}</Box>
      </Box>
      {trend !== undefined && (
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 0.5 }}>
          {trend >= 0 ? <TrendingUp sx={{ fontSize: 16, color: '#22c55e' }} /> : <TrendingDown sx={{ fontSize: 16, color: '#ef4444' }} />}
          <Typography variant="caption" color={trend >= 0 ? '#22c55e' : '#ef4444'} fontWeight={600}>{Math.abs(trend)}%</Typography>
        </Box>
      )}
    </CardContent>
  </Card>
);

const ChartCard: React.FC<{ title: string; subtitle?: string; children: React.ReactNode; height?: number }> = ({ title, subtitle, children, height = 300 }) => (
  <Paper sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
    <Box sx={{ px: 3, pt: 2.5, pb: 1 }}>
      <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
      {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
    </Box>
    <Box sx={{ px: 1, pb: 2, height, minHeight: height, minWidth: 0 }}>{children}</Box>
  </Paper>
);

const BI: React.FC = () => {
  const [data, setData] = useState<BIData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState('year');
  const [tab, setTab] = useState(0);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await api.get(`/admin/reports/bi?period=${period}`);
      if (resp.data.success) setData(resp.data.data);
      else setError('Erro ao carregar dados');
    } catch (err: any) {
      setError(err.message || 'Erro de rede');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const runBackfill = async () => {
    setBackfilling(true);
    setBackfillResult(null);
    try {
      const resp = await api.post('/admin/dashboard/backfill-device-history');
      if (resp.data.success) {
        const { inserted } = resp.data;
        setBackfillResult(`✓ Backfill concluído — pg_pedido: ${inserted.pedido} registos, SISP: ${inserted.sisp} registos. Total: ${inserted.total} (desde ${inserted.desde})`);
        fetchData();
      } else {
        setBackfillResult('Erro: ' + (resp.data.error || 'desconhecido'));
      }
    } catch (err: any) {
      setBackfillResult('Erro: ' + (err.message || 'falha de rede'));
    } finally {
      setBackfilling(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}><CircularProgress size={60} /></Box>;
  if (error) return <Box sx={{ p: 3 }}><Alert severity="error">{error}</Alert></Box>;
  if (!data) return null;

  const { kpis, daily, byEntity, byStatus, byDevice, byReferer, byType, byHour, yearly } = data;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800}>Business Intelligence</Typography>
          <Typography variant="body2" color="text.secondary">Análise completa da plataforma Pagali</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField select size="small" value={period} onChange={(e) => setPeriod(e.target.value)} sx={{ minWidth: 140 }}>
            <MenuItem value="today">Hoje</MenuItem>
            <MenuItem value="week">Últimos 7 dias</MenuItem>
            <MenuItem value="month">Últimos 30 dias</MenuItem>
            <MenuItem value="quarter">Trimestre</MenuItem>
            <MenuItem value="year">Ano</MenuItem>
            <MenuItem value="all">Todos os dados</MenuItem>
          </TextField>
          <Tooltip title="Atualizar"><IconButton onClick={fetchData}><Refresh /></IconButton></Tooltip>
          <Tooltip title="Importar histórico de dispositivos (pg_pedido + SISP)">
            <span>
              <Button
                size="small" variant="outlined" startIcon={backfilling ? <CircularProgress size={14} /> : <Storage />}
                onClick={runBackfill} disabled={backfilling}
                sx={{ fontSize: 12 }}
              >
                {backfilling ? 'A importar...' : 'Backfill histórico'}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>
      {backfillResult && (
        <Alert severity={backfillResult.startsWith('✓') ? 'success' : 'error'} onClose={() => setBackfillResult(null)} sx={{ mb: 2 }}>
          {backfillResult}
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3, borderRadius: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label="Visão Geral" icon={<BarIcon />} iconPosition="start" />
          <Tab label="Receita & Serviços" icon={<AttachMoney />} iconPosition="start" />
          <Tab label="Canais & Dispositivos" icon={<PhoneAndroid />} iconPosition="start" />
          <Tab label="Operacional" icon={<Speed />} iconPosition="start" />
          <Tab label="Crescimento" icon={<TrendingUp />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab 0: Visão Geral */}
      {tab === 0 && (
        <>
          {/* KPI Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 6, sm: 3 }}>
              <KPICard title="Receita Total" value={formatCVE(kpis.receitaTotal)} icon={<AttachMoney sx={{ color: '#22c55e' }} />} color="#22c55e" />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <KPICard title="Total Transações" value={formatNum(kpis.totalTransacoes)} subtitle={`${kpis.totalPagas} pagas`} icon={<Receipt sx={{ color: '#6B46C1' }} />} color="#6B46C1" />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <KPICard title="Taxa de Sucesso" value={`${kpis.taxaSucesso}%`} subtitle={`${kpis.totalFalhadas} falhadas`} icon={<Speed sx={{ color: kpis.taxaSucesso > 80 ? '#22c55e' : '#f59e0b' }} />} color={kpis.taxaSucesso > 80 ? '#22c55e' : '#f59e0b'} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <KPICard title="Novos Utilizadores" value={formatNum(kpis.novosUtilizadores)} subtitle={`Ticket médio: ${formatCVE(kpis.ticketMedio)}`} icon={<People sx={{ color: '#3b82f6' }} />} color="#3b82f6" />
            </Grid>
          </Grid>

          {/* Evolução diária + Estado */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 8 }}>
              <ChartCard title="Evolução de Receita" subtitle="Receita diária no período">
                <ResponsiveContainer width="100%" height="100%" debounce={50}>
                  <AreaChart data={daily}>
                    <defs>
                      <linearGradient id="colorReceita" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6B46C1" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#6B46C1" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="dia" tickFormatter={(v) => new Date(v).toLocaleDateString('pt-PT', { timeZone: 'UTC', day: '2-digit', month: 'short' })} fontSize={11} />
                    <YAxis tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} fontSize={11} />
                    <RTooltip formatter={(v: any) => formatCVE(Number(v))} labelFormatter={(v) => new Date(v).toLocaleDateString('pt-PT', { timeZone: 'UTC' })} />
                    <Area type="monotone" dataKey="receita" stroke="#6B46C1" fill="url(#colorReceita)" strokeWidth={2} name="Receita" />
                  </AreaChart>
                </ResponsiveContainer>
              </ChartCard>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <ChartCard title="Estado das Transações" subtitle="Distribuição por estado">
                <ResponsiveContainer width="100%" height="100%" debounce={50}>
                  <PieChart>
                    <Pie data={byStatus} dataKey="total" nameKey="name" cx="50%" cy="45%" innerRadius={55} outerRadius={80} paddingAngle={3}
                      label={({ name, percent }: any) => percent > 0.05 ? `${name === 'PAID' ? 'Pago' : name === 'PENDING' ? 'Pendente' : name === 'FAILED' ? 'Falhou' : name} ${(percent * 100).toFixed(0)}%` : ''}
                      labelLine={false} fontSize={10}>
                      {byStatus.map((entry) => <Cell key={entry.name} fill={STATUS_COLORS[entry.name] || '#94a3b8'} />)}
                    </Pie>
                    <RTooltip formatter={(v: any) => formatNum(Number(v))} />
                  </PieChart>
                </ResponsiveContainer>
              </ChartCard>
            </Grid>
          </Grid>

          {/* Volume diário transações */}
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <ChartCard title="Volume Diário de Transações" subtitle="Total vs Pagas por dia">
                <ResponsiveContainer width="100%" height="100%" debounce={50}>
                  <BarChart data={daily}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="dia" tickFormatter={(v) => new Date(v).toLocaleDateString('pt-PT', { timeZone: 'UTC', day: '2-digit', month: 'short' })} fontSize={11} />
                    <YAxis fontSize={11} />
                    <RTooltip labelFormatter={(v) => new Date(v).toLocaleDateString('pt-PT', { timeZone: 'UTC' })} />
                    <Legend />
                    <Bar dataKey="transacoes" fill="#e0d5f5" name="Total" radius={[4,4,0,0]} />
                    <Bar dataKey="pagas" fill="#6B46C1" name="Pagas" radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </Grid>
          </Grid>
        </>
      )}

      {/* Tab 1: Receita & Serviços */}
      {tab === 1 && (
        <>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 7 }}>
              <ChartCard title="Top Entidades por Receita" subtitle="As 10 entidades com maior volume" height={350}>
                <ResponsiveContainer width="100%" height="100%" debounce={50}>
                  <BarChart data={byEntity} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} fontSize={11} />
                    <YAxis type="category" dataKey="name" width={120} fontSize={11} />
                    <RTooltip formatter={(v: any) => formatCVE(Number(v))} />
                    <Bar dataKey="receita" fill="#6B46C1" name="Receita" radius={[0,4,4,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <ChartCard title="Receita por Tipo de Serviço" subtitle="Distribuição por categoria" height={350}>
                <ResponsiveContainer width="100%" height="100%" debounce={50}>
                  <PieChart>
                    <Pie data={byType} dataKey="receita" nameKey="name" cx="50%" cy="45%" outerRadius={90}
                      label={({ name, percent }: any) => percent > 0.08 ? `${name} ${(percent * 100).toFixed(0)}%` : ''}
                      labelLine fontSize={10}>
                      {byType.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <RTooltip formatter={(v: any) => formatCVE(Number(v))} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </ChartCard>
            </Grid>
          </Grid>

          {/* Table ranking */}
          <Paper sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
            <Box sx={{ px: 3, py: 2 }}>
              <Typography variant="subtitle1" fontWeight={700}>Ranking de Entidades</Typography>
            </Box>
            <Box sx={{ px: 3, pb: 2 }}>
              {byEntity.map((e, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, borderBottom: '1px solid #f3f4f6' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Chip label={`#${i + 1}`} size="small" sx={{ fontWeight: 700, bgcolor: i < 3 ? '#6B46C115' : '#f3f4f6', color: i < 3 ? '#6B46C1' : '#666' }} />
                    <Typography variant="body2" fontWeight={600}>{e.name}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">{e.transacoes} tx</Typography>
                    <Typography variant="body2" fontWeight={700} color="#22c55e">{formatCVE(e.receita)}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Paper>
        </>
      )}

      {/* Tab 2: Canais & Dispositivos */}
      {tab === 2 && (
        <>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <ChartCard title="Logins por Dispositivo" subtitle="Acessos Mobile vs Web (baseado nos logins)">
                <ResponsiveContainer width="100%" height="100%" debounce={50}>
                  <PieChart>
                    <Pie data={byDevice} dataKey="total" nameKey="name" cx="50%" cy="45%" innerRadius={60} outerRadius={90} paddingAngle={5}
                      label={({ name, percent }: any) => `${name} ${(percent * 100).toFixed(0)}%`} fontSize={12}>
                      {byDevice.map((d, i) => <Cell key={i} fill={d.name === 'Mobile' ? '#6B46C1' : '#00C49F'} />)}
                    </Pie>
                    <RTooltip formatter={(v: any) => formatNum(Number(v))} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </ChartCard>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <ChartCard title="Volume de Acessos por Plataforma" subtitle="Número de logins por dispositivo">
                <ResponsiveContainer width="100%" height="100%" debounce={50}>
                  <BarChart data={byDevice}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" fontSize={12} />
                    <YAxis fontSize={11} />
                    <RTooltip formatter={(v: any) => `${formatNum(Number(v))} logins`} />
                    <Bar dataKey="total" name="Logins" radius={[8,8,0,0]}>
                      {byDevice.map((d, i) => <Cell key={i} fill={d.name === 'Mobile' ? '#6B46C1' : '#00C49F'} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <ChartCard title="Origem das Transações" subtitle="Sites e plataformas que originam pagamentos">
                <ResponsiveContainer width="100%" height="100%" debounce={50}>
                  <BarChart data={byReferer} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" fontSize={11} />
                    <YAxis type="category" dataKey="name" width={100} fontSize={11} />
                    <RTooltip />
                    <Legend />
                    <Bar dataKey="total" fill="#4ECDC4" name="Transações" radius={[0,4,4,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <ChartCard title="Receita por Origem" subtitle="Volume de receita por canal">
                <ResponsiveContainer width="100%" height="100%" debounce={50}>
                  <BarChart data={byReferer} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} fontSize={11} />
                    <YAxis type="category" dataKey="name" width={100} fontSize={11} />
                    <RTooltip formatter={(v: any) => formatCVE(Number(v))} />
                    <Bar dataKey="receita" fill="#6B46C1" name="Receita" radius={[0,4,4,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </Grid>
          </Grid>
        </>
      )}

      {/* Tab 3: Operacional */}
      {tab === 3 && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 6, sm: 3 }}>
              <KPICard title="Pendentes" value={formatNum(kpis.totalPendentes)} icon={<Receipt sx={{ color: '#f59e0b' }} />} color="#f59e0b" />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <KPICard title="Falhadas" value={formatNum(kpis.totalFalhadas)} icon={<Receipt sx={{ color: '#ef4444' }} />} color="#ef4444" />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <KPICard title="Ticket Médio" value={formatCVE(kpis.ticketMedio)} icon={<AttachMoney sx={{ color: '#6B46C1' }} />} color="#6B46C1" />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <KPICard title="Taxa Sucesso" value={`${kpis.taxaSucesso}%`} icon={<Speed sx={{ color: kpis.taxaSucesso > 80 ? '#22c55e' : '#f59e0b' }} />} color={kpis.taxaSucesso > 80 ? '#22c55e' : '#f59e0b'} />
            </Grid>
          </Grid>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 8 }}>
              <ChartCard title="Horário de Pico" subtitle="Transações pagas por hora do dia" height={280}>
                <ResponsiveContainer width="100%" height="100%" debounce={50}>
                  <BarChart data={byHour}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="hora" tickFormatter={(v) => `${v}h`} fontSize={11} />
                    <YAxis fontSize={11} />
                    <RTooltip labelFormatter={(v) => `${v}:00 - ${v}:59`} />
                    <Bar dataKey="total" name="Transações" radius={[4,4,0,0]}>
                      {byHour.map((h, i) => <Cell key={i} fill={h.total > (Math.max(...byHour.map(x => x.total)) * 0.7) ? '#6B46C1' : '#e0d5f5'} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <ChartCard title="Tipo de Serviço" subtitle="Volume por categoria" height={280}>
                <ResponsiveContainer width="100%" height="100%" debounce={50}>
                  <PieChart>
                    <Pie data={byType} dataKey="total" nameKey="name" cx="50%" cy="45%" outerRadius={80}
                      label={({ name, percent }: any) => percent > 0.05 ? `${name}` : ''} fontSize={10}>
                      {byType.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <RTooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </ChartCard>
            </Grid>
          </Grid>

          {/* Conversion funnel */}
          <Paper sx={{ borderRadius: 3, p: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Funil de Conversão</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              {[
                { label: 'Iniciadas', value: kpis.totalTransacoes, color: '#6B46C1', pct: 100 },
                { label: 'Concluídas', value: kpis.totalPagas, color: '#22c55e', pct: kpis.taxaSucesso },
                { label: 'Pendentes', value: kpis.totalPendentes, color: '#f59e0b', pct: kpis.totalTransacoes > 0 ? Math.round((kpis.totalPendentes / kpis.totalTransacoes) * 100) : 0 },
                { label: 'Falhadas', value: kpis.totalFalhadas, color: '#ef4444', pct: kpis.totalTransacoes > 0 ? Math.round((kpis.totalFalhadas / kpis.totalTransacoes) * 100) : 0 },
              ].map((step, i) => (
                <Box key={i} sx={{ flex: 1, minWidth: 150, textAlign: 'center' }}>
                  <Box sx={{ height: 8, borderRadius: 4, bgcolor: 'action.selected', mb: 1, overflow: 'hidden' }}>
                    <Box sx={{ height: '100%', width: `${step.pct}%`, bgcolor: step.color, borderRadius: 4, transition: 'width 0.5s' }} />
                  </Box>
                  <Typography variant="h6" fontWeight={800} color={step.color}>{formatNum(step.value)}</Typography>
                  <Typography variant="caption" color="text.secondary">{step.label} ({step.pct}%)</Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </>
      )}

      {/* Tab 4: Crescimento */}
      {tab === 4 && (
        yearly && yearly.length > 0 ? (
          <>
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, md: 8 }}>
                <ChartCard title="Receita Anual" subtitle="Evolução da receita ao longo dos anos" height={350}>
                  <ResponsiveContainer width="100%" height="100%" debounce={50}>
                    <BarChart data={yearly}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="ano" fontSize={12} />
                      <YAxis tickFormatter={(v: any) => `${(v/1000).toFixed(0)}k`} fontSize={11} />
                      <RTooltip formatter={(v: any) => formatCVE(Number(v))} />
                      <Legend />
                      <Bar dataKey="receita" fill="#6B46C1" name="Receita" radius={[4,4,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <ChartCard title="Transações por Ano" subtitle="Volume total de transações" height={350}>
                  <ResponsiveContainer width="100%" height="100%" debounce={50}>
                    <BarChart data={yearly}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="ano" fontSize={12} />
                      <YAxis fontSize={11} />
                      <RTooltip />
                      <Legend />
                      <Bar dataKey="transacoes" fill="#e0d5f5" name="Total" radius={[4,4,0,0]} />
                      <Bar dataKey="pagas" fill="#22c55e" name="Pagas" radius={[4,4,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Grid>
            </Grid>

            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <ChartCard title="Crescimento de Receita (YoY)" subtitle="Variação percentual ano a ano" height={300}>
                  <ResponsiveContainer width="100%" height="100%" debounce={50}>
                    <LineChart data={yearly.filter((y: any) => y.crescimentoReceita !== null)}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="ano" fontSize={12} />
                      <YAxis tickFormatter={(v: any) => `${v}%`} fontSize={11} />
                      <RTooltip formatter={(v: any) => `${v}%`} />
                      <Line type="monotone" dataKey="crescimentoReceita" stroke="#6B46C1" strokeWidth={3} name="Crescimento Receita %" dot={{ r: 6, fill: '#6B46C1' }} />
                      <Line type="monotone" dataKey="crescimentoTx" stroke="#00C49F" strokeWidth={2} strokeDasharray="5 5" name="Crescimento Tx %" dot={{ r: 4, fill: '#00C49F' }} />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <ChartCard title="Novos Utilizadores por Ano" subtitle="Evolução da base de utilizadores" height={300}>
                  <ResponsiveContainer width="100%" height="100%" debounce={50}>
                    <AreaChart data={yearly}>
                      <defs>
                        <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="ano" fontSize={12} />
                      <YAxis fontSize={11} />
                      <RTooltip />
                      <Area type="monotone" dataKey="novosUsers" stroke="#3b82f6" fill="url(#colorUsers)" strokeWidth={2} name="Novos Utilizadores" />
                    </AreaChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Grid>
            </Grid>

            {/* Yearly summary table */}
            <Paper sx={{ borderRadius: 3, p: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Resumo Anual</Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                      {['Ano', 'Transações', 'Pagas', 'Receita', 'Ticket Médio', 'Novos Users', 'Cresc. Receita', 'Cresc. Tx'].map(h => (
                        <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 12, color: '#6b7280', fontWeight: 600 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[...yearly].reverse().map((y: any) => (
                      <tr key={y.ano} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '10px 12px', fontWeight: 700 }}>{y.ano}</td>
                        <td style={{ padding: '10px 12px' }}>{formatNum(y.transacoes)}</td>
                        <td style={{ padding: '10px 12px' }}>{formatNum(y.pagas)}</td>
                        <td style={{ padding: '10px 12px', fontWeight: 600, color: '#22c55e' }}>{formatCVE(y.receita)}</td>
                        <td style={{ padding: '10px 12px' }}>{y.pagas > 0 ? formatCVE(y.receita / y.pagas) : '-'}</td>
                        <td style={{ padding: '10px 12px' }}>{formatNum(y.novosUsers)}</td>
                        <td style={{ padding: '10px 12px' }}>
                          {y.crescimentoReceita !== null ? (
                            <Chip size="small" label={`${y.crescimentoReceita > 0 ? '+' : ''}${y.crescimentoReceita}%`}
                              color={y.crescimentoReceita > 0 ? 'success' : 'error'} variant="outlined" />
                          ) : '-'}
                        </td>
                        <td style={{ padding: '10px 12px' }}>
                          {y.crescimentoTx !== null ? (
                            <Chip size="small" label={`${y.crescimentoTx > 0 ? '+' : ''}${y.crescimentoTx}%`}
                              color={y.crescimentoTx > 0 ? 'success' : 'error'} variant="outlined" />
                          ) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            </Paper>
          </>
        ) : (
          <Alert severity="info">Sem dados de crescimento anual disponíveis para o período selecionado.</Alert>
        )
      )}
    </Box>
  );
};

export default BI;
